import type { AvatarConfig } from '@perplexicon/validation';

import { avatarLayers } from './avatar-assets.js';

export interface AvatarRendererProps {
  config: AvatarConfig;
  /** Rendered pixel size of the square/circle. */
  size?: number;
  className?: string;
  /**
   * `lazy` for avatars far down a list; `eager` for the ones that are part of
   * the first paint (the header, the profile header, the editor preview).
   */
  loading?: 'lazy' | 'eager';
}

/**
 * Draws a customizable player avatar (APP_SPEC §13) by stacking two
 * pre-rendered layers — the body (base + skin tone + eye shape) and the hair
 * (style + colour) — both authored on the same square canvas, so overlapping
 * them is the whole composite.
 *
 * Both layers are selected by `base`: the bodies have differently-shaped
 * skulls, so each hairstyle is drawn once per body and fitted to that head in
 * the artwork. Nothing is transformed here — stacking the layers is the whole
 * composite. A body with no hair fitted to it yet draws just the body
 * (`avatarLayers`).
 *
 * Purely decorative: it always accompanies a visible username, so the wrapper
 * is `aria-hidden` and both layers carry an empty `alt`
 * (docs/UI_GUIDELINES.md §8, "avatar initials accompany a visible username").
 */
export function AvatarRenderer({
  config,
  size = 40,
  className,
  loading = 'lazy',
}: AvatarRendererProps) {
  const layers = avatarLayers(config);
  const sizes = `${String(size)}px`;

  return (
    <span
      className={className}
      style={{ position: 'relative', display: 'block', width: size, height: size }}
      role="presentation"
      aria-hidden="true"
    >
      {layers.map((layer) => (
        <img
          key={layer.src}
          src={layer.src}
          srcSet={layer.srcSet}
          sizes={sizes}
          width={size}
          height={size}
          alt=""
          loading={loading}
          decoding="async"
          draggable={false}
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
          }}
        />
      ))}
    </span>
  );
}
